import { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { Logger } from "../Logger";
import { ISharePointInteractiveAuthProvider } from "./SharePointAuthService";

/**
 * Implements the interactive step of the SharePoint MSAL flow via Nested App Authentication
 * (NAA) — see doc/npc-32337-sharepoint-msal-naa-auth.md for the full rationale. Unlike
 * `SharePointConsentDialog`, this needs no Office Dialog, no
 * routing components, and no `messageParent` bridge: `pca` was itself constructed via
 * `createNestablePublicClientApplication` (see `createNestableSharePointMsalClient`), which lets
 * the Office host broker the interactive prompt natively, in-process.
 *
 * Only usable when `OfficeWrapper.isNestedAppAuthSupported()` is true; `SharePointAuthServiceFactory`
 * constructs this instead of `SharePointConsentDialog` when that check passes.
 */
export class SharePointNaaInteractiveAuthProvider implements ISharePointInteractiveAuthProvider {
    constructor(private pca: IPublicClientApplication, private logger: Logger, private loginHint?: string) {}

    async acquireTokenInteractive(scopes: string[]): Promise<AuthenticationResult> {
        this.logger.info(
            `SharePointNaaInteractiveAuthProvider [MSAL + NAA]: calling pca.acquireTokenPopup(scopes=[${scopes.join(
                ", "
            )}], loginHint=${
                this.loginHint ? "set" : "none"
            }) - the Office host brokers the sign-in natively (no Office Dialog window)`
        );
        try {
            const result = await this.pca.acquireTokenPopup(
                this.loginHint ? { scopes, loginHint: this.loginHint } : { scopes }
            );
            this.logger.info(
                `SharePointNaaInteractiveAuthProvider [MSAL + NAA]: acquireTokenPopup succeeded (fromCache=${result.fromCache})`
            );
            return result;
        } catch (error) {
            this.logger.error("SharePointNaaInteractiveAuthProvider [MSAL + NAA]: acquireTokenPopup failed", error);
            throw error;
        }
    }
}
